/**
 * Утилиты для работы с localStorage
 */

// Все ключи Zustand stores которые могут накапливаться
const STORAGE_KEYS = [
  'user-storage',
  'subscription-limits-storage',
  'in-app-notifications-storage',
  'pricing-storage',
  'achievements-storage',
  'chat-storage',
  'image-storage',
  'tts-storage',
  'communication-style-storage',
  'team-storage',
  'pricing-etag',
  'pending_payment_id',
  'low_tokens_notification_dismissed_at',
  'free_low_tokens_notification_dismissed_at',
];

/**
 * Очистить все данные пользователя из localStorage
 */
export const clearUserStorage = () => {
  try {
    // Очищаем основное хранилище пользователя
    localStorage.removeItem('user-storage');
    
    // Очищаем хранилище лимитов подписки
    localStorage.removeItem('subscription-limits-storage');
    
    // Очищаем pending payment если есть
    localStorage.removeItem('pending_payment_id');
    
    return true;
  } catch (error) {
    console.error('Failed to clear user storage:', error);
    return false;
  }
};

/**
 * Полная очистка всех данных приложения из localStorage
 * Используется при критических проблемах с памятью в Telegram Mini App
 */
export const clearAllAppStorage = () => {
  try {
    STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ All app storage cleared');
    return true;
  } catch (error) {
    console.error('Failed to clear all app storage:', error);
    return false;
  }
};

/**
 * Получить размер localStorage в байтах (приблизительно)
 */
export const getLocalStorageSize = (): number => {
  let total = 0;
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += localStorage[key].length + key.length;
    }
  }
  return total * 2; // UTF-16 = 2 bytes per character
};

/**
 * Проверить и очистить localStorage если превышен лимит
 * Telegram Mini App на iOS имеет жёсткие ограничения памяти
 * @param maxSizeKB - максимальный размер в килобайтах (по умолчанию 2MB)
 */
export const cleanupIfNeeded = (maxSizeKB = 2048): boolean => {
  const sizeBytes = getLocalStorageSize();
  const sizeKB = sizeBytes / 1024;
  
  if (sizeKB > maxSizeKB) {
    console.warn(`⚠️ localStorage size (${sizeKB.toFixed(1)}KB) exceeds limit (${maxSizeKB}KB), cleaning up...`);
    
    // Очищаем накопительные данные, сохраняя важные настройки
    const keysToClean = [
      'in-app-notifications-storage',
      'pricing-storage',
      'achievements-storage',
    ];
    
    keysToClean.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Также очищаем Service Worker кэш
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.active?.postMessage('clearCache');
      }).catch(() => {});
    }
    
    console.log(`✅ Cleanup complete. New size: ${(getLocalStorageSize() / 1024).toFixed(1)}KB`);
    return true;
  }
  
  return false;
};

/**
 * Очистить кэш и перезагрузить приложение
 */
export const clearCacheAndReload = () => {
  clearUserStorage();
  window.location.reload();
};

/**
 * Получить данные пользователя из localStorage
 */
export const getUserFromStorage = () => {
  try {
    const data = localStorage.getItem('user-storage');
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    return parsed?.state?.user || null;
  } catch (error) {
    console.error('❌ Failed to get user from storage:', error);
    return null;
  }
};

/**
 * Вывести информацию о данных в localStorage (для отладки)
 */
export const debugStorage = () => {
  console.group('🔍 LocalStorage Debug Info');
  
  const user = getUserFromStorage();
  console.log('User data:', user);
  
  const pendingPayment = localStorage.getItem('pending_payment_id');
  console.log('Pending payment:', pendingPayment);
  
  const limitsData = localStorage.getItem('subscription-limits-storage');
  console.log('Subscription limits:', limitsData ? JSON.parse(limitsData) : null);
  
  console.groupEnd();
};

/**
 * Проверить актуальность данных в localStorage
 * @param serverUser - данные пользователя с сервера
 * @returns true если данные синхронизированы, false если нужна очистка
 */
export const validateStorageSync = (serverUser: any): boolean => {
  const localUser = getUserFromStorage();
  
  if (!localUser || !serverUser) return true;
  
  // Проверяем подписку
  const localTier = localUser.subscriptionTier || 'free';
  const serverTier = serverUser.subscriptionTier || 'free';
  
  if (localTier !== serverTier) {
    return false;
  }
  
  // Проверяем токены (разница не должна быть больше 10%)
  const localTokens = localUser.tokens || 0;
  const serverTokens = serverUser.tokens || 0;
  const tokensDiff = Math.abs(localTokens - serverTokens);
  const maxAllowedDiff = Math.max(localTokens, serverTokens) * 0.1;
  
  if (tokensDiff > maxAllowedDiff && tokensDiff > 10) {
    return false;
  }
  
  return true;
};

